import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from './../axiosInstance.js';
import { Button, Loader, Modal, Text } from '@mantine/core';

const PLATFORM_META = {
  imdb: { label: 'IMDb', icon: '/imdb_small_icon.png' },
  kinopoisk: { label: 'Kinopoisk', icon: '/kinopoisk_small_icon.png' },
  rottenTomatoes: { label: 'Rotten Tomatoes (critics)', icon: '/rottentomatoes_icon.png' },
  rottenTomatoesAudience: { label: 'Rotten Tomatoes (audience)', icon: '/rottentomatoes_icon.png' },
  amazon: { label: 'Amazon', icon: '/amazon_icon.png' },
  goodreads: { label: 'Goodreads', icon: '/goodreads_icon.png' },
  reddit: { label: 'Reddit', icon: '/reddit_icon.png' },
  appleBooks: { label: 'Apple Books', icon: '/applebooks_icon.png' },
  steam: { label: 'Steam', icon: '/steam_icon.png' },
  metacritic: { label: 'Metacritic (critics)', icon: '/metacritic_icon.png' },
  metacriticUser: { label: 'Metacritic (users)', icon: '/metacritic_icon.png' },
  livelib: { label: 'Livelib', icon: '/livelib_icon.png' },
  litres: { label: 'Litres', icon: '/litres_icon.png' },
};

// Inline SVG line chart. Kept dep-free — admin-only surface, one bundle less.
const RatingChart = ({ points, label, icon }) => {
  if (!points || points.length === 0) return null;
  const W = 360;
  const H = 110;
  const padL = 34;
  const padR = 10;
  const padT = 10;
  const padB = 22;
  const minT = points[0].t;
  const maxT = points[points.length - 1].t;
  const dt = Math.max(1, maxT - minT);
  const values = points.map((p) => p.v);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const pad = rawMax === rawMin ? Math.max(0.5, rawMax * 0.05) : (rawMax - rawMin) * 0.1;
  const minV = rawMin - pad;
  const maxV = rawMax + pad;
  const dv = Math.max(0.01, maxV - minV);
  const x = (t) => padL + ((t - minT) / dt) * (W - padL - padR);
  const y = (v) => H - padB - ((v - minV) / dv) * (H - padT - padB);
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const fmtDate = (ms) => new Date(ms).toISOString().slice(0, 10);
  return (
    <div className="my-2">
      <div className="flex items-center gap-2 mb-1">
        {icon && <img src={icon} alt={label} className="w-4 h-4 rounded-sm" />}
        <span className="text-xs">{label}</span>
        <span className="text-xs text-gray-500">
          — {points.length} point{points.length !== 1 ? 's' : ''}, {rawMin.toFixed(2)} → {rawMax.toFixed(2)}
        </span>
      </div>
      <svg width={W} height={H} style={{ background: 'var(--mantine-color-body)', border: '1px solid var(--mantine-color-default-border)', borderRadius: 4 }}>
        <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#ccc" strokeWidth="1" />
        <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#ccc" strokeWidth="1" />
        <text x={padL - 4} y={y(rawMax) + 3} textAnchor="end" fontSize="9" fill="#888">{rawMax.toFixed(2)}</text>
        <text x={padL - 4} y={y(rawMin) + 3} textAnchor="end" fontSize="9" fill="#888">{rawMin.toFixed(2)}</text>
        <text x={padL} y={H - 6} textAnchor="start" fontSize="9" fill="#888">{fmtDate(minT)}</text>
        <text x={W - padR} y={H - 6} textAnchor="end" fontSize="9" fill="#888">{fmtDate(maxT)}</text>
        <path d={d} fill="none" stroke="#1971c2" strokeWidth="1.5" />
        {points.map((p, i) => (
          <circle key={i} cx={x(p.t)} cy={y(p.v)} r="2" fill="#1971c2">
            <title>{`${fmtDate(p.t)}: ${p.v} (${p.votes} votes)`}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
};

// AdminRatingHistoryButton renders a button that opens a modal with one
// line chart per platform for the artRatingHistory rows the daily
// UpdateMoviesRatings job snapshots.
const AdminRatingHistoryButton = ({ artId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || loaded || loading) return;
    setLoading(true);
    setError(null);
    axiosInstance
      .get(`/admin/arts/${artId}/ratingHistory`)
      .then((res) => {
        setHistory(res.data.history || []);
        setLoaded(true);
      })
      .catch((e) => setError(e.response?.data || e.message))
      .finally(() => setLoading(false));
  }, [open, artId, loaded, loading]);

  const byPlatform = useMemo(() => {
    const m = {};
    history.forEach((e) => {
      if (!m[e.platform]) m[e.platform] = [];
      m[e.platform].push({ t: new Date(e.dateTime).getTime(), v: e.rating, votes: e.votesCount });
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.t - b.t));
    return m;
  }, [history]);

  return (
    <>
      <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setOpen(true)}>
        View rating history
      </Button>
      <Modal opened={open} onClose={() => setOpen(false)} title="Rating history" size="lg">
        {loading && (
          <div className="flex justify-center py-4">
            <Loader size="sm" />
          </div>
        )}
        {error && <Text c="red" size="sm">{String(error)}</Text>}
        {!loading && !error && loaded && Object.keys(byPlatform).length === 0 && (
          <Text size="sm" c="dimmed">No rating history recorded for this art yet.</Text>
        )}
        {!loading && !error && Object.keys(byPlatform).length > 0 && (
          <div className="flex flex-col">
            {Object.entries(byPlatform).map(([platform, points]) => {
              const meta = PLATFORM_META[platform] || { label: platform, icon: null };
              return <RatingChart key={platform} points={points} label={meta.label} icon={meta.icon} />;
            })}
          </div>
        )}
      </Modal>
    </>
  );
};

export default AdminRatingHistoryButton;
